import { motion } from "framer-motion";
import { 
  Play, 
  TrendingUp, 
  Bell, 
  Calculator,
  Eye
} from "lucide-react";
import { useEffect, useState } from "react";

function AnimatedValue({ value, prefix = "" }: { value: number; prefix?: string }) {
  const [current, setCurrent] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (hasAnimated) return;
    
    const duration = 1500;
    const steps = 40;
    const increment = value / steps;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      setCurrent(Math.min(Math.floor(increment * step), value));
      if (step >= steps) {
        clearInterval(timer);
        setHasAnimated(true);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value, hasAnimated]);

  return (
    <span className="tabular-nums">
      {prefix}{current.toLocaleString("pt-BR")}
    </span>
  );
}

export function DemoSection() {
  return (
    <section className="relative py-20 sm:py-28 bg-gradient-to-b from-background via-muted/30 to-background overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-600 text-sm font-medium mb-6">
            <Eye className="h-4 w-4" />
            Veja na prática
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-wide text-foreground mb-4">
            TUDO CONECTADO.{" "}
            <span className="text-primary">
              TUDO CALCULADO.
            </span>
          </h2>
          <p className="text-2xl sm:text-3xl font-bold text-primary">
            TUDO CLARO.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Timer Demo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative group"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/10 to-purple-500/10 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative bg-card border border-border rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Play className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground">Timer em Funcionamento</h3>
                  <p className="text-sm text-muted-foreground">Projeto: Website Corporativo</p>
                </div>
              </div>
              
              <div className="bg-muted/50 rounded-xl p-6 text-center mb-4 border border-border">
                <p className="font-display text-5xl sm:text-6xl font-bold text-foreground tracking-wide mb-2">
                  02:34:17
                </p>
                <p className="text-primary font-medium">
                  R$ <AnimatedValue value={213} prefix="" />,50 acumulados
                </p>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Taxa hora: R$ 85/h</span>
                <span className="text-success">● Registrando...</span>
              </div>
            </div>
          </motion.div>

          {/* Margin Demo */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative group"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-success/10 to-teal-500/10 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative bg-card border border-border rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
                  <Calculator className="h-5 w-5 text-success" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground">Margem Calculada</h3>
                  <p className="text-sm text-muted-foreground">Atualização automática</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Valor do projeto</span>
                  <span className="text-foreground font-bold">R$ 5.000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Horas investidas</span>
                  <span className="text-foreground font-bold">45h</span>
                </div>
                <div className="h-px bg-border" />
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Valor hora efetivo</span>
                  <span className="text-success font-bold text-lg">R$ 111,11/h</span>
                </div>
                <div className="bg-success/10 rounded-lg p-3 text-center border border-success/20">
                  <span className="text-success font-bold">+30% acima da sua meta</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Projection Demo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative group"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-warning/10 to-orange-500/10 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative bg-card border border-border rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground">Projeção do Mês</h3>
                  <p className="text-sm text-muted-foreground">Baseada no seu ritmo atual</p>
                </div>
              </div>
              
              <div className="bg-muted/50 rounded-xl p-4 mb-4 border border-border">
                <div className="flex justify-between items-end mb-3">
                  <div>
                    <p className="text-muted-foreground text-sm">Projeção final</p>
                    <p className="text-2xl font-bold text-foreground">
                      R$ <AnimatedValue value={18500} />
                    </p>
                  </div>
                  <span className="text-success text-sm bg-success/10 px-2 py-1 rounded">
                    +23% vs mês anterior
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <motion.div 
                    initial={{ width: 0 }}
                    whileInView={{ width: "82%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="bg-primary h-2 rounded-full"
                  />
                </div>
                <p className="text-right text-sm text-muted-foreground mt-1">82% da meta</p>
              </div>
            </div>
          </motion.div>

          {/* Alerts Demo */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative group"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-destructive/10 to-pink-500/10 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative bg-card border border-border rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                  <Bell className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground">Alertas Inteligentes</h3>
                  <p className="text-sm text-muted-foreground">Proteção automática</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-warning/10 border border-warning/20">
                  <div className="w-2 h-2 rounded-full bg-warning animate-pulse" />
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Concentração de risco:</span> 65% da receita vem de 1 cliente
                  </p>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Pagamento previsto:</span> R$ 3.500 em 5 dias
                  </p>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-success/10 border border-success/20">
                  <div className="w-2 h-2 rounded-full bg-success" />
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Meta diária:</span> Você está 15% acima do ritmo
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
